import { type RefObject, useLayoutEffect, useRef, useState } from 'react';

const TRAILING_TEXT_GAP_PX = 4;

interface UseTwoLineMessageOptions {
  enabled: boolean;
  message: string;
  trailingText: string;
}

interface UseTwoLineMessageResult {
  messageRef: RefObject<HTMLParagraphElement>;
  visibleMessage: string;
}

/**
 * Fit a message within two lines while reserving space for trailing inline text.
 *
 * @param {object} options - Hook options.
 * @param {boolean} options.enabled - Whether truncation is enabled.
 * @param {string} options.message - Full message to measure.
 * @param {string} options.trailingText - Inline text rendered after the message.
 * @returns {object} Message element ref and text to render.
 */
function useTwoLineMessage({
  enabled,
  message,
  trailingText,
}: UseTwoLineMessageOptions): UseTwoLineMessageResult {
  const messageRef = useRef<HTMLParagraphElement>(null);
  const [truncatedMessage, setTruncatedMessage] = useState('');

  useLayoutEffect(() => {
    if (!enabled || !messageRef.current) return undefined;
    const messageElement = messageRef.current;
    let animationFrame: number | null = null;

    /** Measure and truncate the message to two lines. */
    function truncateToTwoLines() {
      const width = messageElement.clientWidth;
      if (!width) return;
      const styles = window.getComputedStyle(messageElement);
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) return;

      context.font = `${styles.fontStyle} ${styles.fontVariant} ${styles.fontWeight} ${styles.fontSize} ${styles.fontFamily}`;
      const words = message.split(/\s+/).filter(Boolean);
      const visibleWords = [];
      let line = '';
      let lineNumber = 1;
      const reservedWidth = context.measureText(`... ${trailingText}`).width + TRAILING_TEXT_GAP_PX;

      words.some((word) => {
        const nextLine = line ? `${line} ${word}` : word;
        const availableWidth = lineNumber === 2 ? width - reservedWidth : width;
        if (context.measureText(nextLine).width <= availableWidth) {
          line = nextLine;
          visibleWords.push(word);
          return false;
        }
        if (lineNumber === 1) {
          lineNumber = 2;
          line = word;
          visibleWords.push(word);
          return false;
        }
        return true;
      });

      const nextMessage =
        visibleWords.length < words.length ? `${visibleWords.join(' ')}...` : message;
      setTruncatedMessage((currentMessage) =>
        currentMessage === nextMessage ? currentMessage : nextMessage,
      );
    }

    /** Measure once after the browser finishes the current resize layout. */
    function scheduleTruncation() {
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        animationFrame = null;
        truncateToTwoLines();
      });
    }

    truncateToTwoLines();
    window.addEventListener('resize', scheduleTruncation);
    return () => {
      window.removeEventListener('resize', scheduleTruncation);
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
    };
  }, [enabled, message, trailingText]);

  return {
    messageRef,
    visibleMessage: enabled ? truncatedMessage || message : message,
  };
}

export { useTwoLineMessage };
